import { type ReactNode, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListFilter } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import {
    clientHeatmapExportUrl,
    type ClientHeatmapParams,
    type ClientHeatmapReport,
} from '@/features/reports/api';
import { HEATMAP_COPY } from '@/features/reports/copy';
import { ActivityTable } from '@/features/reports/heatmap/ActivityTable';
import { heatmapFileName } from '@/features/reports/heatmap/labels';
import { ReportCard } from '@/features/reports/parts';
import { clientHeatmapLogQuery, reportsKeys } from '@/features/reports/queries';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { Register } from '@/layouts/Register';

const COPY = HEATMAP_COPY.log;

/**
 * The activity log under the heatmap's views (ALS-88): the source of truth they are built from,
 * kept on screen because an audit will ask for it. It shows the latest three; Show all opens the
 * window's whole log as a paged register searchable by client, and Export downloads all of it as
 * the other report exports do.
 */
export function ActivityLogCard({
    report,
    params,
}: {
    report: ClientHeatmapReport;
    params: ClientHeatmapParams;
}) {
    const [expanded, setExpanded] = useState(false);
    const total = report.logTotal;
    const latest = report.latestLog;
    const folds = total > latest.length;

    // Still offered once expanded, even if the window has since shrunk, so the reader can fold back.
    const toggle =
        folds || expanded ? (
            <Button variant="outline" size="sm" onClick={() => setExpanded((open) => !open)}>
                {expanded ? COPY.showLatest(latest.length) : COPY.showAll(total)}
            </Button>
        ) : null;

    const actions = (
        <>
            {toggle}
            <ExportButton
                url={clientHeatmapExportUrl(params)}
                fileName={heatmapFileName(params.weeks)}
                disabled={total === 0}
            />
        </>
    );

    if (expanded) {
        // Keyed on the window and manager: a new scope starts again from page one with no search.
        return (
            <LogRegister key={`${params.weeks}|${params.rmId}`} params={params} actions={actions} />
        );
    }

    return (
        <ReportCard
            title={COPY.title}
            subtitle={folds ? COPY.latest(latest.length, total) : COPY.count(total)}
            actions={actions}
        >
            <p className="text-muted-foreground mb-3 text-sm">{COPY.note}</p>
            {total === 0 ? (
                <EmptyState
                    icon={<ListFilter />}
                    title={COPY.empty.title}
                    body={COPY.empty.body(params.rmId === undefined)}
                />
            ) : (
                <ActivityTable rows={latest} />
            )}
        </ReportCard>
    );
}

/** The whole window's log, paged on the server and searchable by client. */
function LogRegister({ params, actions }: { params: ClientHeatmapParams; actions: ReactNode }) {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    // Null until the register has measured how many rows fit, so one page of the right size is asked for.
    const [size, setSize] = useState<number | null>(null);

    const filters = { ...params, page, size: size ?? 0, q: search };
    const log = useQuery(clientHeatmapLogQuery(filters, size !== null));
    const data = log.data;

    return (
        <Register
            title={COPY.title}
            actions={actions}
            search={search}
            onSearchChange={(value) => {
                setSearch(value);
                setPage(1);
            }}
            searchLabel={COPY.search}
            status={log.isPending ? 'pending' : log.isError ? 'error' : 'ready'}
            onRetry={() =>
                void queryClient.invalidateQueries({
                    queryKey: reportsKeys.clientHeatmapLog(filters),
                })
            }
            rows={data?.rows ?? []}
            total={data?.total ?? 0}
            pageKey={search}
            noMatch={COPY.noMatch}
            paging={{
                page,
                onPageChange: setPage,
                matched: data?.matched ?? 0,
                onPageSizeChange: setSize,
            }}
        >
            {(rows) => <ActivityTable rows={rows} />}
        </Register>
    );
}
